import { RiFlashlightLine } from "@remixicon/react";
import React, {
    ReactNode, useEffect, useState,
} from "react";
import { Dropdown } from "react-bootstrap";
import { AiFillCheckCircle } from "react-icons/ai";
import { FaRocketchat, FaTelegramPlane } from "react-icons/fa";
import { GiGears } from "react-icons/gi";
import { GrAction } from "react-icons/gr";
import { IconType } from "react-icons/lib";
import { PiMicrosoftTeamsLogo } from "react-icons/pi";
import {
    SiAtlassian, SiGithub, SiHubspot, SiIntercom, SiOkta,
    SiSalesforce, SiSlack, SiZapier, SiZendesk,
} from "react-icons/si";
import {
    ActionExecuteStartCondition,
    Chat, DisplayFormSource,
    FieldTypedValue,
    FieldTypedValueBase,
    FieldTypedValueInput,
    FormField, FormFieldType, IntegrationType, IntegrationView,
    TicketInput,
    useGetIntegrationsQuery, useRunIntegrationMutation,
    useSubmitIntegrationMutation,
} from "../../../__generated__/api";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import { useTheme } from "../../../contexts/Theme";
import ActionExecute from "../../action-execute/ActionExecute";
import { Ticket } from "../../chat/Chat";
import Confirm from "../../confirm/Confirm";
import FilterPanel from "../../filters/FilterPanel";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import Loading from "../../loading/Loading";
import DropdownTheme from "../../theme/dropdown/Dropdown";
import Styles from "./integrations.module.scss";

interface IntegrationsProps {
    chatId?: string;
    ticket?: Ticket;
    hasStarted: boolean;
    shouldIncludeServices?: boolean;
    onExecuted?: (ticket: TicketInput) => void;
    text?: string;
    className?: string;
    title?: string;
    ticketInput?: TicketInput;
    metricPathPrefix?: string[];
}

const ICONS: Record<IntegrationType, IconType> = {
    [IntegrationType.Okta]: SiOkta,
    [IntegrationType.Zendesk]: SiZendesk,
    [IntegrationType.Salesforce]: SiSalesforce,
    [IntegrationType.Github]: SiGithub,
    [IntegrationType.GithubApp]: SiGithub,
    [IntegrationType.Hubspot]: SiHubspot,
    [IntegrationType.Zapier]: SiZapier,
    [IntegrationType.Intercom]: SiIntercom,
    [IntegrationType.Slack]: SiSlack,
    [IntegrationType.SlackAgent]: SiSlack,
    [IntegrationType.SlackBot]: SiSlack,
    [IntegrationType.MicrosoftTeams]: PiMicrosoftTeamsLogo,
    [IntegrationType.RocketChat]: FaRocketchat,

    [IntegrationType.Integromat]: GiGears,
    [IntegrationType.Atlassian]: SiAtlassian,
    [IntegrationType.Freshdesk]: GiGears,
    [IntegrationType.OneLogin]: GiGears,
    [IntegrationType.ServiceNow]: GiGears,
    [IntegrationType.Workato]: GiGears,

    [IntegrationType.Service]: GiGears,
    [IntegrationType.Approvals]: AiFillCheckCircle,

};

export default function Integrations(
    {
        chatId, ticket, hasStarted, shouldIncludeServices, onExecuted, text, className, title, ticketInput, metricPathPrefix,
    }: IntegrationsProps,
): ReactNode {
    const { data, loading, error } = useGetIntegrationsQuery({
        variables: {
            chatId,
            shouldIncludeServices: shouldIncludeServices ?? true,
        },
    });

    const theme = useTheme();
    const [runIntegration] = useRunIntegrationMutation();
    const [submit] = useSubmitIntegrationMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [integrationId, setIntegrationId] = useState<string>();

    const [formId, setFormId] = useState<string>();

    const [values, setValues] = useState<FieldTypedValueInput[]>([]);

    const [form, setForm] = useState<IntegrationView>();

    const [actionId, setActionId] = useState<string>();

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+i",
            context: "chat",
            description: "Calls an integration",
            group: "Conversations",
            handler: () => {
                setShouldShow(true);
            },
        });
    });

    useEffect(() => {
        setValues([]);
        setForm(undefined);
    }, [showForm]);

    if (loading) {
        return null;
    }
    if (!data || error) {
        return undefined;
    }

    let { forms, integrations } = data;
    if (!hasStarted) {
        forms = forms.filter(row => row.actionSettings?.startCondition === ActionExecuteStartCondition.BeforeOrAfterStarted);
        integrations = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const startInteration = async (integrationId: string, formId?: string, search?: string) => {
        setShowForm(true);
        setFormId(formId);
        setIntegrationId(integrationId);
        setShouldShow(false);
        const integration = await runIntegration({
            variables: {
                chatId,
                integrationId,
                formId,
                search,
                source: DisplayFormSource.Portal,
                currentValues: values,
            },
        });
        setForm(integration.data?.runIntegration as IntegrationView | undefined);

        if (!formId) {
            const asyncField = integration.data?.runIntegration?.fields
                .find(formField => formField.type === FormFieldType.AsyncDropdown);
            const selectedFormId = asyncField?.defaultValue?.string;
            if (asyncField && selectedFormId) {
                setFormId(selectedFormId);

                setValues(current => {
                    const copy = [...current];
                    const index = copy.findIndex(fieldValue => fieldValue.fieldId === selectedFormId);
                    const value = {
                        string: selectedFormId,
                        fieldId: asyncField.id,
                        type: asyncField.type,
                    } as FieldTypedValueInput;
                    if (index >= 0) {
                        copy[index] = value;
                    } else {
                        copy.push(value);
                    }
                    return copy;
                });
            }
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const startAction = async (formId?: string) => {
        setActionId(formId);
        setShouldShow(false);
    };

    const onUpdate = (updatedFormId?: string, search?: string) => {
        if (!integrationId) {
            return;
        }
        startInteration(integrationId, updatedFormId ?? formId, search);
    };

    const handleSetValue = (value: FieldTypedValue, field: FormField) => {
        setValues(current => {
            const copy = [...current];
            const index = copy.findIndex(fieldValue => fieldValue.fieldId === value.fieldId);
            if (index >= 0) {
                // eslint-disable-next-line no-param-reassign
                copy[index] = value;
            } else {
                copy.push(value);
            }
            return copy;
        });
        if (value.type === FormFieldType.AsyncDropdown) {
            onUpdate(value.string as string);
        } else if (value.type === FormFieldType.SearchText) {
            onUpdate(undefined, value.string as string);
        } else if (field.optionsSettings?.dynamicChoiceContext || (
            value.type === FormFieldType.Dropdown
            && form?.fields.some(formField => formField.optionsSettings?.dynamicChoiceContext)
        )) {
            const selectedFormId = formId
                ?? form?.fields.find(formField => formField.type === FormFieldType.AsyncDropdown)?.defaultValue?.string;
            onUpdate(selectedFormId as string | undefined);
        }
    };

    const onCreate = () => {
        (async () => {
            await submit({
                variables: {
                    chatId,
                    integrationId,
                    form: {
                        id: formId,
                        values,
                    },
                },
            });
        })();
        setShowForm(false);
    };

    return (
        <>
            {actionId && (
                <ActionExecute
                    actionId={actionId}
                    onHide={() => setActionId(undefined)}
                    chat={ticket as Chat}
                    chatId={chatId}
                    onExecuted={onExecuted}
                    ticketInput={ticketInput}
                    metricPath={metricPathPrefix ? [...metricPathPrefix, "execute", actionId] : undefined}
                />
            )}
            {showForm && (
                <Confirm
                    shouldShow={showForm}
                    onHide={() => setShowForm(false)}
                    confirmText={form?.submit}
                    className={`${Styles["container-dialog"]}`}
                    confirmMetricPath={metricPathPrefix ? [...metricPathPrefix, "execute", form?.id] : undefined}
                    onConfirm={onCreate}
                >
                    {!form ? (
                        <Loading />
                    ) : (
                        <div className="d-flex flex-column">
                            <h5 className="mb-4">
                                {form.title}
                            </h5>
                            <div className={`overflow-auto flex-grow-1 flex-shrink-1 ${Styles.container}`}>

                                {form.fields.map((field, index) => (

                                    <div className={theme.isV3 ? "form-group" : "mb-3"} key={field.id}>
                                        <FieldTypeValueDisplay
                                            field={field}
                                            fieldNameClassName={theme.isV3 ? "form-label" : "text-gray-500"}
                                            defaultValue={field.defaultValue ? {
                                                value: field.defaultValue as FieldTypedValueBase,
                                            } : undefined}
                                            onChange={value => handleSetValue(value, field)}
                                            getCurrentForm={() => values}
                                            integrationId={integrationId}
                                            inputClassName={theme.isV3 ? "form-control" : undefined}
                                            shouldFocus={index === 0}
                                            className="mb-3"
                                        />

                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </Confirm>
            )}
            {theme.isV3 && (integrations?.isNotEmpty || forms.isNotEmpty) ? (
                <Dropdown
                    onToggle={shouldShowNext => setShouldShow(shouldShowNext)}
                    show={shouldShow}
                    drop={theme.isV3 ? undefined : "up"}
                >
                    <DropdownTheme.Toggle
                        variant="secondary"
                        size="small"
                        prefixIcon={<RiFlashlightLine />}
                        onClick={() => setShouldShow(value => !value)}
                        title={title}
                    />
                    <Dropdown.Menu className={Styles.dropdown}>
                        <FilterPanel
                            isMulti={false}
                            searchMinimum={10}
                            categories={[
                                forms?.isNotEmpty ? {
                                    label: "Actions",
                                    options: forms?.sortBy("name").map(action => ({
                                        label: (
                                            <>
                                                <RiFlashlightLine className="icon-secondary m3e-1" />
                                                {action.name}
                                            </>
                                        ),
                                        value: action.id,
                                        text: action.name,
                                    })) ?? [],
                                } : undefined,
                                integrations?.isNotEmpty ? {
                                    label: "Services",
                                    options: integrations?.sortBy("label").map(integration => {
                                        const Icon = ICONS[integration.type as IntegrationType] ?? GiGears;
                                        return {
                                            label: (
                                                <>
                                                    <Icon className="icon-secondary m3e-1" />
                                                    {integration.label}
                                                </>
                                            ),
                                            value: integration.id,
                                            text: integration.label,
                                        };
                                    }) ?? [],
                                } : undefined,
                            ].filterNull()}
                            className={className}
                            onSelected={selectedId => {
                                if (metricPathPrefix) {
                                    LoggingHandler.instance.metric([
                                        ...metricPathPrefix,
                                        selectedId.startsWith("urn:fq:form") ? "action" : "integration",
                                        selectedId,
                                    ].filterFalsy());
                                }
                                if (selectedId.startsWith("urn:fq:form")) {
                                    startAction(selectedId);
                                } else {
                                    startInteration(selectedId);
                                }
                            }}
                        />
                    </Dropdown.Menu>
                </Dropdown>

            ) : (

                (integrations?.isNotEmpty || forms.isNotEmpty) && (
                    <Dropdown
                        onToggle={shouldShowNext => setShouldShow(shouldShowNext)}
                        show={shouldShow}
                        drop="up"
                    >

                        <Dropdown.Toggle
                            variant=""
                            className={`menu-small btn-none p-1 ${className}`}
                            onClick={() => setShouldShow(value => !value)}
                            title={title}
                        >
                            <GrAction />
                            {" "}
                            {text}
                        </Dropdown.Toggle>

                        <Dropdown.Menu className={Styles.dropdown}>
                            {forms.isNotEmpty && (
                                <>

                                    <Dropdown.Header>Actions</Dropdown.Header>
                                    {forms.sortBy("name").map(action => (
                                        <Dropdown.Item
                                            key={action.id}
                                            eventKey={action.id}
                                            onClick={() => startAction(action.id)}
                                        >
                                            <FaTelegramPlane />
                                            {" "}
                                            {action.name}
                                        </Dropdown.Item>
                                    ))}
                                    {integrations?.isNotEmpty && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Header>Services</Dropdown.Header>
                                        </>
                                    )}

                                </>
                            )}
                            {integrations?.sortBy("label").map(integration => {
                                const Icon = ICONS[integration.type as IntegrationType] ?? GiGears;
                                return (
                                    <Dropdown.Item
                                        key={integration.id}
                                        eventKey={integration.id}
                                        onClick={() => startInteration(integration.id)}
                                    >
                                        <Icon />
                                        {" "}
                                        {integration.label}
                                    </Dropdown.Item>
                                );
                            })}

                        </Dropdown.Menu>
                    </Dropdown>
                )
            )}

        </>

    );
}

Integrations.defaultProps = {
    ticket: undefined,
    shouldIncludeServices: true,
    chatId: undefined,
    onExecuted: undefined,
    text: "",
    className: "",
    title: "",
    ticketInput: undefined,
    metricPathPrefix: undefined,
};
